// src/components/EfectoEstacional.js
//
// Efecto estacional del sitio público (nieve / estrellas) dibujado en un
// <canvas> fijo que no captura clics. La configuración vive en la BD
// (ConfiguracionTexto, clave EFECTO_ESTACIONAL, JSON) y se edita desde
// Panel → Integraciones → "Efecto estacional" (solo SuperAdmin).
//
// - Si la clave no existe, el JSON es inválido o "activo" es false → no se muestra.
// - Respeta rango de fechas (desde/hasta), "soloInicio" y prefers-reduced-motion.
// - Se pausa cuando la pestaña no está visible.
// - Si "permitirOcultar" es true, el visitante puede apagarlo con un botón;
//   la preferencia se guarda por versión de configuración (si el admin cambia
//   la configuración, el efecto vuelve a mostrarse).

import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import { getConfiguracionTexto } from '../services/configuracionTextoApi';

export const CLAVE_EFECTO = 'EFECTO_ESTACIONAL';

export const EFECTO_DEFAULT = {
  activo: false,
  tipo: 'nieve',          // nieve | estrellas
  intensidad: 60,         // 1–100
  velocidad: 1,           // 0.3–3
  color: '#ffffff',
  desde: '',              // 'YYYY-MM-DD' opcional
  hasta: '',              // 'YYYY-MM-DD' opcional
  soloInicio: false,      // solo en "/"
  permitirOcultar: true,  // botón para que el visitante lo apague
};

const clamp = (v, min, max, def) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : def;
};

export function normalizarEfecto(raw) {
  let c = raw;
  if (typeof raw === 'string') { try { c = JSON.parse(raw); } catch { c = {}; } }
  c = c || {};
  return {
    activo:          c.activo === true,
    tipo:            ['nieve', 'estrellas'].includes(c.tipo) ? c.tipo : 'nieve',
    intensidad:      clamp(c.intensidad, 1, 100, EFECTO_DEFAULT.intensidad),
    velocidad:       clamp(c.velocidad, 0.3, 3, EFECTO_DEFAULT.velocidad),
    color:           /^#[0-9a-fA-F]{6}$/.test(c.color || '') ? c.color : EFECTO_DEFAULT.color,
    desde:           typeof c.desde === 'string' ? c.desde : '',
    hasta:           typeof c.hasta === 'string' ? c.hasta : '',
    soloInicio:      c.soloInicio === true,
    permitirOcultar: c.permitirOcultar !== false,
  };
}

function dentroDeFechas({ desde, hasta }) {
  const hoy = new Date();
  const ymd = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
  if (desde && ymd < desde) return false;
  if (hasta && ymd > hasta) return false;
  return true;
}

const LS_OCULTO = 'fenagro_efecto_oculto_v';
const leerOculto = () => { try { return localStorage.getItem(LS_OCULTO); } catch { return null; } };
const guardarOculto = (v) => { try { v == null ? localStorage.removeItem(LS_OCULTO) : localStorage.setItem(LS_OCULTO, v); } catch {} };

// ── Motor de partículas ─────────────────────────────────────────────────────
// Se ejecuta en un Web Worker con OffscreenCanvas (hilo aparte): así las
// transiciones pesadas de la página (cambio de slide del hero, blur, carga de
// imágenes) no lo congelan. El movimiento es por tiempo real (delta), no por
// cuadro, para que la velocidad sea constante aunque baje el FPS.
// IMPORTANTE: el motor se serializa con .toString() para el worker → debe ser
// autocontenido: sin imports, sin variables externas y sin for...of/spread
// (Babel los transpila a helpers externos que no existirían en el worker).
function motorEfecto(scope) {
  var canvas = null, ctx = null, cfg = null;
  var w = 0, h = 0, dpr = 1, contenido = false;
  var parts = [], raf = 0, corriendo = false, ultimo = 0, t = 0;
  var pedirCuadro = scope.requestAnimationFrame
    ? function (fn) { return scope.requestAnimationFrame(fn); }
    : function (fn) { return scope.setTimeout(function () { fn(scope.performance.now()); }, 16); };
  var cancelarCuadro = scope.cancelAnimationFrame
    ? function (id) { scope.cancelAnimationFrame(id); }
    : function (id) { scope.clearTimeout(id); };

  function crear(enCualquierAltura) {
    var estrella = cfg.tipo === 'estrellas';
    return {
      x: Math.random() * w,
      y: enCualquierAltura ? Math.random() * h : -10,
      r: estrella ? 1 + Math.random() * 2.2 : 1.2 + Math.random() * 3.3,
      vy: (estrella ? 15 + Math.random() * 30 : 24 + Math.random() * 66) * cfg.velocidad, // px/seg
      fase: Math.random() * Math.PI * 2,
      amp: 10 + Math.random() * 25,                                                     // px de vaivén
      alfa: 0.45 + Math.random() * 0.5,
    };
  }

  function poblar() {
    var movil = w < 600;
    var n = Math.round(cfg.intensidad * 2 * (movil ? 0.5 : 1) * (contenido ? 0.5 : 1));
    parts = [];
    for (var i = 0; i < n; i++) parts.push(crear(true));
  }

  function dimensionar(nw, nh, ndpr) {
    w = nw; h = nh; dpr = ndpr;
    canvas.width = Math.max(1, Math.floor(w * dpr));
    canvas.height = Math.max(1, Math.floor(h * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    poblar();
  }

  function estrella(x, y, r) {
    ctx.beginPath();
    for (var i = 0; i < 4; i++) {
      var a = (Math.PI / 2) * i;
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(a) * r * 2.2, y + Math.sin(a) * r * 2.2);
    }
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, y, r * 0.6, 0, Math.PI * 2);
    ctx.fill();
  }

  function cuadro(ahora) {
    if (!corriendo) return;
    var dt = ultimo ? (ahora - ultimo) / 1000 : 0.016;
    if (dt > 0.1) dt = 0.1; // tras una pausa larga, no "saltar"
    ultimo = ahora;
    t += dt;
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = cfg.color;
    ctx.strokeStyle = cfg.color;
    ctx.lineWidth = 0.8;
    var esEstrella = cfg.tipo === 'estrellas';
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      p.y += p.vy * dt;
      var x = p.x + Math.sin(t * 1.2 + p.fase) * p.amp;
      if (p.y > h + 10) { parts[i] = crear(false); continue; }
      if (esEstrella) {
        ctx.globalAlpha = p.alfa * (0.6 + 0.4 * Math.sin(t * 3 + p.fase));
        estrella(x, p.y, p.r);
      } else {
        ctx.globalAlpha = p.alfa;
        ctx.beginPath();
        ctx.arc(x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    raf = pedirCuadro(cuadro);
  }

  function iniciar() {
    if (corriendo) return;
    corriendo = true; ultimo = 0;
    raf = pedirCuadro(cuadro);
  }
  function detener() { corriendo = false; cancelarCuadro(raf); }

  return {
    init: function (c, nw, nh, ndpr, nconfig, ncontenido) {
      canvas = c; ctx = c.getContext('2d'); cfg = nconfig; contenido = ncontenido;
      dimensionar(nw, nh, ndpr);
      iniciar();
    },
    resize: dimensionar,
    play: iniciar,
    pause: detener,
  };
}

// Código del worker: motor + mensajes.
const WORKER_SRC =
  'var motor = (' + motorEfecto.toString() + ')(self);\n' +
  'self.onmessage = function (e) {\n' +
  '  var d = e.data;\n' +
  '  if (d.cmd === "init") motor.init(d.canvas, d.w, d.h, d.dpr, d.config, d.contenido);\n' +
  '  else if (d.cmd === "resize") motor.resize(d.w, d.h, d.dpr);\n' +
  '  else if (d.cmd === "pause") motor.pause();\n' +
  '  else if (d.cmd === "play") motor.play();\n' +
  '};\n';

let workerUrl = null;
function crearWorker() {
  try {
    if (!workerUrl) workerUrl = URL.createObjectURL(new Blob([WORKER_SRC], { type: 'text/javascript' }));
    return new Worker(workerUrl);
  } catch { return null; }
}

// ── Lienzo (también lo usa la vista previa del admin) ───────────────────────
function Lienzo({ config, contenido }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const medir = () => {
      const p = contenido ? canvas.parentElement : null;
      const w = p ? p.clientWidth : window.innerWidth;
      const h = p ? p.clientHeight : window.innerHeight;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      return { w, h };
    };
    const cfg = { tipo: config.tipo, intensidad: config.intensidad, velocidad: config.velocidad, color: config.color };
    const { w, h } = medir();

    // 1) Hilo aparte (OffscreenCanvas + Worker) 2) Respaldo en el hilo principal
    let worker = null, motor = null;
    if (typeof canvas.transferControlToOffscreen === 'function' && typeof Worker !== 'undefined') {
      worker = crearWorker();
      if (worker) {
        try {
          const off = canvas.transferControlToOffscreen();
          worker.postMessage({ cmd: 'init', canvas: off, w, h, dpr, config: cfg, contenido }, [off]);
        } catch { worker.terminate(); worker = null; }
      }
    }
    if (!worker) {
      motor = motorEfecto(window);
      motor.init(canvas, w, h, dpr, cfg, contenido);
    }

    const onResize = () => {
      const m = medir();
      if (worker) worker.postMessage({ cmd: 'resize', w: m.w, h: m.h, dpr });
      else motor.resize(m.w, m.h, dpr);
    };
    const onVisibilidad = () => {
      const cmd = document.hidden ? 'pause' : 'play';
      if (worker) worker.postMessage({ cmd });
      else motor[cmd]();
    };
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibilidad);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibilidad);
      if (worker) worker.terminate();
      if (motor) motor.pause();
    };
  }, [config.tipo, config.intensidad, config.velocidad, config.color, contenido]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={contenido
        ? { position: 'absolute', inset: 0, pointerEvents: 'none' }
        : { position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1250 }}
    />
  );
}

// Un canvas transferido a OffscreenCanvas no se puede reutilizar: si cambia la
// configuración (vista previa del admin) se monta un canvas nuevo vía key.
export function LienzoEfecto({ config, contenido = false }) {
  const key = [config.tipo, config.intensidad, config.velocidad, config.color, contenido].join('|');
  return <Lienzo key={key} config={config} contenido={contenido} />;
}

// ── Efecto global del sitio público ─────────────────────────────────────────
export default function EfectoEstacional({ pathname }) {
  const [config, setConfig] = useState(null);
  const [version, setVersion] = useState(null);
  const [oculto, setOculto] = useState(false);

  useEffect(() => {
    let cancelado = false;
    getConfiguracionTexto(CLAVE_EFECTO)
      .then(t => {
        if (cancelado) return;
        const v = String(t?.versionActual ?? '');
        setVersion(v);
        setConfig(normalizarEfecto(t?.contenido));
        setOculto(leerOculto() === v);
      })
      .catch(() => { if (!cancelado) setConfig(null); }); // sin clave o backend caído → sin efecto
    return () => { cancelado = true; };
  }, []);

  if (!config || !config.activo || !dentroDeFechas(config)) return null;
  if (config.soloInicio && pathname !== '/') return null;
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return null;

  const alternar = () => {
    const nuevo = !oculto;
    setOculto(nuevo);
    guardarOculto(nuevo ? version : null);
  };

  return (
    <>
      {!oculto && <LienzoEfecto config={config} />}
      {config.permitirOcultar && (
        <Box sx={{ position: 'fixed', left: 16, bottom: 16, zIndex: 1251 }}>
          <Tooltip title={oculto ? 'Mostrar efecto' : 'Ocultar efecto'} placement="right">
            <IconButton onClick={alternar} size="small" aria-label={oculto ? 'Mostrar efecto' : 'Ocultar efecto'}
              sx={{
                background: 'rgba(26,77,46,0.85)', color: '#fff',
                opacity: oculto ? 0.55 : 0.9,
                '&:hover': { background: 'rgba(26,77,46,1)', opacity: 1 },
              }}>
              <AcUnitIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </>
  );
}
